import Scheduler, { SchedulerRef, SchedulerTypes, } from "devextreme-react/scheduler";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Appointment from "./Appointment";
import "./ChecklistScheduleCalendar";
import ChecklistsApi from "../api/ChecklistApi";
import { ODataFilterService } from "../../../core/service/odataFilterService";
import { Checklist } from "../model/ChecklistDtos";
import { ScheduleFormModal } from "./ScheduleFormModal";
import ChecklistScheduleTooltip from "./ChecklistScheduleTooltip";
import { AppointmentDblClickEvent } from "devextreme/ui/scheduler";
import { FormState } from "./ScheduleForm";
import { useChecklistScheduleMapper } from "../hook/useChecklistScheduleMapper";
import { useSearchParams } from "react-router-dom";
import { AppointmentMapper } from "../mapper/scheduleAppointmentMapper";
import { Card } from "../../../core/components";

const views: SchedulerTypes.ViewType[] = ["month"];

interface ScheduleChecklistsProps {
    occupiedHeight?: number
    schedulerClassName?: string
    addButtonClassName?: string
}

export interface SchedulerData {
    id?: number;
    idChecklist?: number;
    text: string;
    startDate: Date;
    endDate?: Date;
    description?: string;
    recurrenceRule?: string;
    allDay: boolean;
}

export const ScheduleChecklists = forwardRef((props: ScheduleChecklistsProps, ref) => {
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [schedulerData, setSchedulerData] = useState<SchedulerData[]>();
    const schedulerRef = useRef<SchedulerRef>(null)

    const [searchParams] = useSearchParams()
    const checklistId = searchParams.get("checklistId")

    const [isAppointmentModalOpen, setAppointmentModalOpen] = useState(false);
    const [appointmentModalInitialData, setAppointmentModalInitialData] = useState<Partial<FormState>>({});

    const { toRequest } = useChecklistScheduleMapper()

    useImperativeHandle(ref, () => schedulerRef.current)

    useEffect(() => {
        const fetchChecklists = async () => {
            try {
                fetchData()
            } catch (error) {
            }
        };
        fetchChecklists();
    }, []);

    const onSubmit = async (formState: FormState): Promise<void> => {
        const idChecklist = formState.idChecklist;

        if (!idChecklist) return
        if (!formState.startDate) return

        const request = toRequest(formState)
        if (!request) return

        if (!formState.idChecklistSchedule) {
            if (await ChecklistsApi.scheduleChecklist(request)) {
                await fetchData();
            }
        } else {
            if (await ChecklistsApi.updateChecklistSchedule(request)) {
                await fetchData()
            }
        }
    }

    const fetchData = async () => {
        const data = await ChecklistsApi.getChecklists(true, ODataFilterService.createQueryFilterParams({
            skip: 0,
            take: 100
        }))

        setChecklists(data.items ?? [])
        setSchedulerData(AppointmentMapper.mapChecklistsToSchedulerData(data.items ?? []));
    };

    const handleAppointmentDoubleClick = (e: AppointmentDblClickEvent) => {
        e.cancel = true;
    };

    const handleDelete = async (appointmentId: number): Promise<void> => {
        if (await ChecklistsApi.deleteSchedule(appointmentId)) {
            fetchData();
        }
    };

    const onAppointmentFormOpening = async (e: SchedulerTypes.AppointmentFormOpeningEvent): Promise<void> => {
        e.cancel = true;

        setAppointmentModalInitialData({
            idChecklistSchedule: e.appointmentData?.id,
            idChecklist: Number(e.appointmentData?.description),
            startDate: e.appointmentData?.startDate?.toString() ?? undefined
        });

        setAppointmentModalOpen(true);
    };

    const showChecklistScheduleModal = (e: SchedulerTypes.AppointmentClickEvent) => {
        e.cancel = true

        setAppointmentModalInitialData({
            idChecklistSchedule: e.appointmentData.id,
            idChecklist: e.appointmentData.idChecklist
        })

        schedulerRef.current?.instance?.().hideAppointmentTooltip();

        setAppointmentModalOpen(true)
    }

    return (
        <Card padding={"0"}>
            <Scheduler
                className="border-radius scheduler-height"
                ref={schedulerRef}
                firstDayOfWeek={1}
                views={views}
                dataSource={schedulerData}
                defaultCurrentView="month"
                startDayHour={8}
                defaultCurrentDate={new Date()}
                crossScrollingEnabled
                onAppointmentRendered={(e) => {
                    const checklistId = searchParams.get("checklistId");
                    if (checklistId && Number(checklistId) === e.appointmentData.idChecklist) {
                        e.appointmentElement.style.border = "2px solid #ddd";
                        e.appointmentElement.style.backgroundColor = "#0A7469";
                    }
                    e.appointmentElement.style.borderRadius = "8px";
                }}
                editing={{
                    allowDragging: false,
                    allowResizing: false,
                }}
                onAppointmentDblClick={handleAppointmentDoubleClick}
                onAppointmentClick={showChecklistScheduleModal}
                appointmentTooltipRender={(props) =>
                    <ChecklistScheduleTooltip
                        {...props}
                        handleDelete={handleDelete}
                        onAppointmentClick={showChecklistScheduleModal}
                    />
                }
                appointmentComponent={Appointment}
                onAppointmentFormOpening={onAppointmentFormOpening} />
            <ScheduleFormModal
                isOpen={isAppointmentModalOpen}
                onClose={() => setAppointmentModalOpen(false)}
                onDelete={handleDelete}
                initialData={appointmentModalInitialData}
                checklists={checklists}
                onSubmit={onSubmit}
            />
        </Card>
    );
})
